import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Building, TrendingUp, Users, ChevronDown, Mail, Clock, Globe, BarChart3, Zap, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEffect, useRef, useState } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db, auth } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "@/hooks/use-toast";
import { loadPostsByLang } from "@/blog/loader";
import type { LoadedPost } from "@/blog/types";
import { LanguageSelector } from "@/components/ui/language-selector";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";
import SignInForm from "@/components/auth/SignInForm";
import { AuthLink } from "@/components/navigation/AuthLink";
import { HeroMechanism } from "@/components/home/HeroMechanism";
import { EcosystemCarousel } from "@/components/home/EcosystemCarousel";
import { useLanguage } from "@/contexts/LanguageContext";
import { FeaturedListingsCarousel } from "@/components/home/FeaturedListingsCarousel";

const newsletterSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

type NewsletterForm = z.infer<typeof newsletterSchema>;

interface Deal {
  id: string;
  title: string;
  industry: string;
  location: string;
  description: string;
  imageUrl?: string;
  ebitda?: number;
  revenue?: number;
  foundedYear?: number;
  favoriteCount: number;
  favoritedBy?: string[];
}

// Helper component for Manage Cookies button (needs hook context)
const ManageCookiesButton = () => {
  const { resetConsent } = useCookieConsent();
  return (
    <button 
      onClick={resetConsent} 
      className="hover:text-accent transition-colors text-left"
    >
      Manage Cookies
    </button>
  );
};

const Index = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [favoriteLoading, setFavoriteLoading] = useState<string | null>(null);
  const [blogPosts, setBlogPosts] = useState<LoadedPost[]>([]);
  const [whyCardsVisible, setWhyCardsVisible] = useState(false);
  const whyCardsRef = useRef<HTMLDivElement>(null);
  const blogLang: "en" | "fr" = language === "fr" ? "fr" : "en";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NewsletterForm>({
    resolver: zodResolver(newsletterSchema),
  });

  useEffect(() => {
    fetchFeaturedDeals();
    fetchBlogPosts();
  }, [blogLang]);

  useEffect(() => {
    const cards = whyCardsRef.current;
    if (!cards) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setWhyCardsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35, rootMargin: "0px 0px -8% 0px" },
    );

    observer.observe(cards);
    return () => observer.disconnect();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const posts = await loadPostsByLang(blogLang);
      setBlogPosts(posts.slice(0, 3));
    } catch (error) {
      console.error("Error loading blog posts:", error);
    }
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.trim().split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  const fetchFeaturedDeals = async () => {
    try {
      const dealsRef = collection(db, "deals");
      const q = query(dealsRef, orderBy("favoriteCount", "desc"), limit(3));
      const snapshot = await getDocs(q);
      const dealsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Deal[];
      setDeals(dealsData);
    } catch (error) {
      console.error("Error fetching deals:", error);
      // Set mock data as fallback
      setDeals([
        {
          id: "1",
          title: "Technology SaaS",
          industry: "Technology",
          location: "Florida, USA",
          description: "Recurring B2B software with low churn",
          ebitda: 250000,
          favoriteCount: 24,
          favoritedBy: [],
        },
        {
          id: "2",
          title: "Green Agriculture",
          industry: "Agriculture",
          location: "Florida, USA",
          description: "Sustainable solutions with solid margins",
          revenue: 1000000,
          favoriteCount: 12,
          favoritedBy: [],
        },
        {
          id: "3",
          title: "Industrial Manufacturing",
          industry: "Manufacturing",
          location: "Florida, USA",
          description: "Established company with strong reputation",
          foundedYear: 1995,
          favoriteCount: 35,
          favoritedBy: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (dealId: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to favorite deals",
        variant: "destructive",
      });
      return;
    }

    setFavoriteLoading(dealId);
    try {
      const dealRef = doc(db, "deals", dealId);
      const deal = deals.find((d) => d.id === dealId);
      const isFavorited = deal?.favoritedBy?.includes(user.id);

      if (isFavorited) {
        await updateDoc(dealRef, {
          favoritedBy: arrayRemove(user.id),
          favoriteCount: (deal?.favoriteCount || 1) - 1,
        });
        toast({
          title: "Removed from Favorites",
          description: "Deal has been removed from your favorites",
        });
      } else {
        await updateDoc(dealRef, {
          favoritedBy: arrayUnion(user.id),
          favoriteCount: (deal?.favoriteCount || 0) + 1,
        });
        toast({
          title: "Added to Favorites",
          description: "Deal has been added to your favorites",
        });
      }

      // Refresh deals
      await fetchFeaturedDeals();
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast({
        title: "Error",
        description: "Failed to update favorites. Please try again.",
        variant: "destructive",
      });
    } finally {
      setFavoriteLoading(null);
    }
  };

  const onNewsletterSubmit = async (data: NewsletterForm) => {
    try {
      // In production, integrate with email service (SendGrid, Mailchimp, etc.)
      console.log("Newsletter signup:", data.email);

      toast({
        title: "Successfully Subscribed!",
        description: "You've been added to our newsletter.",
      });
      reset();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="bg-primary/95 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center header-logo-wrap">
              <Link to="/" className="hover:opacity-80 transition-opacity">
                <img
                  src="/noBgColor.png"
                  alt="AcquizGroup"
                  className="ag-logo-header"
                />
              </Link>
            </div>

            {/* Mobile controls */}
<div className="flex items-center gap-2 md:hidden">
<LanguageSelector variant="light" />
<Button
type="button"
variant="ghost"
size="icon"
className="text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
aria-expanded={mobileMenuOpen}
>
{mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
</Button>
</div>
            <div className="hidden md:flex space-x-8 items-center">
              {/* Services */}
              <DropdownMenu>
                <DropdownMenuTrigger className="text-primary-foreground/90 hover:text-primary-foreground transition-colors flex items-center gap-1">
                  {t("nav.services")}
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-popover border-border shadow-lg z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/landing/sellers" className="cursor-pointer">
                      {t("nav.sellers")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/buyers" className="cursor-pointer">
                      {t("nav.buyers")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/consultants" className="cursor-pointer">
                      {t("nav.consultants")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/brokers" className="cursor-pointer">
                      {t("nav.brokers")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/lenders" className="cursor-pointer">
                      {t("nav.lenders")}
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Resources */}
              <DropdownMenu>
                <DropdownMenuTrigger className="text-primary-foreground/90 hover:text-primary-foreground transition-colors flex items-center gap-1">
                  {t("nav.resources")}
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-popover border-border shadow-lg z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/listings" className="cursor-pointer">
                      {t("nav.for_sale")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/blog" className="cursor-pointer">
                      Blog
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/resources/e-learning" className="cursor-pointer">
                      E-learning
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/about" className="text-primary-foreground/90 hover:text-primary-foreground transition-colors">
                {t("nav.about")}
              </Link>

              {/* Our Partners Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="text-primary-foreground/90 hover:text-primary-foreground transition-colors flex items-center gap-1">
                  {t("nav.our_experts")}
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-popover border-border shadow-lg z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/landing/brokers" className="cursor-pointer">
                      {t("nav.brokers")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/lenders" className="cursor-pointer">
                      {t("nav.lenders")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/partners" className="cursor-pointer">
                      {t("index.business_lawyers")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/partners" className="cursor-pointer">
                      {t("index.immigration_lawyers")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/partners" className="cursor-pointer">
                      CPA
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/partners" className="cursor-pointer">
                      {t("nav.notary")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/landing/partners" className="cursor-pointer">
                      {t("nav.tax_advisor")}
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Portal Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="text-primary-foreground/90 hover:text-primary-foreground transition-colors flex items-center gap-1">
                  Portal
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-popover border-border shadow-lg z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/login?next=/admin-dashboard" className="cursor-pointer w-full">
                      Admin Portal
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/login?next=/partners/dashboard" className="cursor-pointer w-full">
                      Business Partners Portal
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/login?next=/staff-dashboard" className="cursor-pointer w-full">
                      Staff Portal
                    </Link>
                  </DropdownMenuItem>
                  {user && (
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <AuthLink portalKey="dashboard" className="w-full">
                        My Dashboard
                      </AuthLink>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/early-access"
                className="text-primary-foreground/90
                  hover:text-primary-foreground
                  transition-colors font-semibold
                  text-[#CEAB65] border border-[#CEAB65]/40
                  px-3 py-1.5 rounded-full text-sm"
              >
                {language === 'fr'
                  ? '🎖 Membres Fondateurs'
                  : '🎖 Founding Members'}
              </Link>

              <LanguageSelector />

              <Link to="/login">
                <Button size="sm" variant="accent">
                  {t("index.sign_in")}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-primary border-t border-white/10 px-4 py-4 space-y-3">
            <p className="text-xs uppercase text-primary-foreground/50 font-semibold tracking-wider">Services</p>
            <Link to="/landing/sellers" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.sellers")}</Link>
            <Link to="/landing/buyers" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.buyers")}</Link>
            <Link to="/landing/consultants" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.consultants")}</Link>
            <Link to="/landing/brokers" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.brokers")}</Link>
            <Link to="/landing/lenders" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.lenders")}</Link>

            <Separator className="bg-white/10" />
            <p className="text-xs uppercase text-primary-foreground/50 font-semibold tracking-wider">{t("nav.resources")}</p>
            <Link to="/listings" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.for_sale")}</Link>
            <Link to="/blog" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
            <Link to="/resources/e-learning" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>E-learning</Link>

            <Separator className="bg-white/10" />
            <Link to="/about" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.about")}</Link>

            <Separator className="bg-white/10" />
            <p className="text-xs uppercase text-primary-foreground/50 font-semibold tracking-wider">{t("nav.our_experts")}</p>
            <Link to="/landing/brokers" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.brokers")}</Link>
            <Link to="/landing/lenders" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.lenders")}</Link>
            <Link to="/landing/partners" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("index.business_lawyers")}</Link>
            <Link to="/landing/partners" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("index.immigration_lawyers")}</Link>
            <Link to="/landing/partners" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>CPA</Link>
            <Link to="/landing/partners" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.notary")}</Link>
            <Link to="/landing/partners" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>{t("nav.tax_advisor")}</Link>

            <Separator className="bg-white/10" />
            <p className="text-xs uppercase text-primary-foreground/50 font-semibold tracking-wider">Portal</p>
            <Link to="/login?next=/admin-dashboard" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>Admin Portal</Link>
            <Link to="/login?next=/partners/dashboard" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>Business Partners Portal</Link>
            <Link to="/login?next=/staff-dashboard" className="block text-sm text-primary-foreground/90 hover:text-primary-foreground py-1" onClick={() => setMobileMenuOpen(false)}>Staff Portal</Link>

            <Separator className="bg-white/10" />
            <Link
              to="/early-access"
              className="block text-sm font-bold
                text-[#CEAB65] py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              🎖 {language === 'fr'
                ? 'Membres Fondateurs'
                : 'Founding Members'}
            </Link>

            <Separator className="bg-white/10" />
            <div className="flex items-center gap-3 pt-2">
              <LanguageSelector />
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                <Button size="sm" variant="accent">{t("index.sign_in")}</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section — dynamic mechanism */}
      <HeroMechanism />

      {/* Financing Redefinition Section */}
      <section className="py-20 bg-muted/30 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t("index.financing.title")}
            </h2>
            <div className="w-24 h-1 bg-accent mx-auto mb-8"></div>
            <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              {t("index.financing.subtitle")}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Box 1 - Expanding Access */}
            <Card className="p-8 text-center hover:shadow-lg transition-shadow border-border">
              <CardHeader>
                <Globe className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-xl mb-4 text-primary">{t("index.financing.box1_title")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-card-foreground">
                  {t("index.financing.box1_desc")}
                </CardDescription>
              </CardContent>
            </Card>

            {/* Box 2 - Clarity Through Numbers */}
            <Card className="p-8 text-center hover:shadow-lg transition-shadow border-border">
              <CardHeader>
                <BarChart3 className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-xl mb-4 text-primary">{t("index.financing.box2_title")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-card-foreground">
                  {t("index.financing.box2_desc")}
                </CardDescription>
              </CardContent>
            </Card>

            {/* Box 3 - Performance Without Prospecting */}
            <Card className="p-8 text-center hover:shadow-lg transition-shadow border-border">
              <CardHeader>
                <Zap className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-xl mb-4 text-primary">{t("index.financing.box3_title")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-card-foreground">
                  {t("index.financing.box3_desc")}
                </CardDescription>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-12">
            <Link to="/general-landing">
              <Button size="lg" variant="default">
                {t("index.financing.learn_more")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Ecosystem Section — infinite left-scrolling carousel */}
      <section className="py-20 bg-background overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">{t("index.ecosystem.title")}</h2>
            <p className="text-xl text-muted-foreground">{t("index.ecosystem.subtitle")}</p>
          </div>
        </div>
        <EcosystemCarousel />
        <div className="text-center mt-12">
          <Link to="/general-landing">
            <Button size="lg" variant="default">
              {t("index.financing.learn_more")}
            </Button>
          </Link>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">{t("index.why.title")}</h2>
            <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
              {t("index.why.subtitle")}
            </p>
          </div>
          <div ref={whyCardsRef} className="grid md:grid-cols-3 gap-8">
            <Card className={`text-center p-8 hover:shadow-lg transition-shadow border-border motion-reduce:animate-none motion-reduce:opacity-100 ${whyCardsVisible ? "animate-sky-drop" : "opacity-0"}`}>
              <CardHeader>
                <Users className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-xl mb-4">{t("index.why.card1_title")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-card-foreground">
                  {t("index.why.card1_desc")}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className={`text-center p-8 hover:shadow-lg transition-shadow border-border motion-reduce:animate-none motion-reduce:opacity-100 ${whyCardsVisible ? "animate-sky-drop [animation-delay:2000ms]" : "opacity-0"}`}>
              <CardHeader>
                <TrendingUp className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-xl mb-4">{t("index.why.card2_title")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-card-foreground">
                  {t("index.why.card2_desc")}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className={`text-center p-8 hover:shadow-lg transition-shadow border-border motion-reduce:animate-none motion-reduce:opacity-100 ${whyCardsVisible ? "animate-sky-drop [animation-delay:4000ms]" : "opacity-0"}`}>
              <CardHeader>
                <Building className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-xl mb-4">{t("index.why.card3_title")}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-card-foreground">
                  {t("index.why.card3_desc")}
                </CardDescription>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-12">
            <Link to="/general-landing">
              <Button size="lg" variant="default">
                {t("index.financing.learn_more")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Opportunities */}
      <section className="py-20 bg-background overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              {language === "fr" ? "Opportunités d'Acquisition en Vedette" : "Featured Acquisition Opportunities"}
            </h2>
            <p className="text-xl text-muted-foreground">
              {language === "fr"
                ? "Découvrez des entreprises à la recherche de leur prochain leader."
                : "Discover businesses looking for their next leader."}
            </p>
          </div>

        </div>

        <div className="mb-12">
          <FeaturedListingsCarousel />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Link to="/listings">
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#b8924f] to-[#d4af6a] hover:from-[#a07f42] hover:to-[#c9a05d] text-white"
              >
                {language === "fr" ? "Voir Toutes les Opportunités" : "View All Opportunities"}{" "}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                {blogLang === "fr" ? "Articles en vedette" : "Featured Articles"}
              </h2>
              <p className="text-muted-foreground">
                {blogLang === "fr" ? "Nos derniers conseils et analyses." : "Our latest advice and insights."}
              </p>
            </div>
            
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {blogPosts.map((post) => (
              <Card
                key={post.frontmatter.slug}
                className="hover:shadow-lg transition-shadow border-border flex flex-col"
              >
                {post.frontmatter.hero && (
                  <img src={post.frontmatter.hero} alt="" className="w-full h-48 object-cover rounded-t-lg" />
                )}
                <CardHeader className="flex-1">
                  {post.frontmatter.tags && post.frontmatter.tags[0] && (
                    <Badge variant="secondary" className="w-fit mb-2">
                      {post.frontmatter.tags[0]}
                    </Badge>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Clock className="h-4 w-4" />
                    <span>{estimateReadTime(post.content)} min</span>
                  </div>
                  <CardTitle className="text-xl mb-3 leading-snug">{post.frontmatter.title}</CardTitle>
                  {post.frontmatter.excerpt && (
                    <CardDescription className="text-sm line-clamp-3">{post.frontmatter.excerpt}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="mt-auto">
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                    <span>AcquizGroup</span>
                    <span>
                      {new Date(post.frontmatter.date).toLocaleDateString(blogLang === "fr" ? "fr-FR" : "en-US")}
                    </span>
                  </div>
                  <Link to={`/blog/${post.frontmatter.slug}`}>
                    <Button variant="outline" className="w-full">
                      {blogLang === "fr" ? "Lire l'article" : "Read article"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center">
            <Link to="/blog">
              <Button size="lg" variant="accent">
                {blogLang === "fr" ? "Voir tous les articles" : "View all articles"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/90 to-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <Mail className="h-12 w-12 text-accent mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("index.newsletter.title")}</h2>
            <p className="text-primary-foreground/80 text-lg">
              {t("index.newsletter.subtitle")}
            </p>
          </div>
          <form onSubmit={handleSubmit(onNewsletterSubmit)} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-grow">
                <Input
                  type="email"
                  placeholder={t("index.newsletter.placeholder")}
                  {...register("email")}
                  className="bg-background text-foreground"
                />
                {errors.email && <p className="text-destructive text-sm mt-1">{errors.email.message}</p>}
              </div>
              <Button type="submit" variant="accent" disabled={isSubmitting} className="sm:w-auto">
                {isSubmitting ? t("index.newsletter.subscribing") : t("index.newsletter.subscribe")}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="py-20 bg-muted/30 text-center">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">{t("index.partner_cta.title")}</h2>
          <p className="text-muted-foreground text-lg">
            {t("index.partner_cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/landing/lenders">
              <Button size="lg" variant="accent" className="min-w-56">
                {t("index.partner_cta.im_lender")}
              </Button>
            </Link>
            <Link to="/register/partner">
              <Button size="lg" className="min-w-56">
                {t("index.partner_cta.im_partner")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="footer-logo-wrap mb-8">
            <img src="/noBgColor.png" alt="AcquizGroup" className="ag-logo-footer" />
          </div>
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-accent">{t("index.footer.about")}</h3>
              <p className="text-primary-foreground/80 text-sm">
                {t("index.footer.about_desc")}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t("index.footer.transactions")}</h3>
              <ul className="space-y-2 text-sm text-primary-foreground/80">
                <li>
                  <Link to="/landing/sellers" className="hover:text-accent transition-colors">
                    {t("index.footer.sell")}
                  </Link>
                </li>
                <li>
                  <Link to="/landing/buyers" className="hover:text-accent transition-colors">
                    {t("index.footer.buy")}
                  </Link>
                </li>
                <li>
                  <Link to="/register/brokers" className="hover:text-accent transition-colors">
                    {t("index.footer.create_listing")}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t("index.footer.partners")}</h3>
              <ul className="space-y-2 text-sm text-primary-foreground/80">
                <li>
                  <Link to="/landing/consultants" className="hover:text-accent transition-colors">
                    {t("index.footer.consultants")}
                  </Link>
                </li>
                <li>
                  <Link to="/landing/partners" className="hover:text-accent transition-colors">
                    {t("index.footer.partners_link")}
                  </Link>
                </li>
                <li>
                  <Link to="/landing/lenders" className="hover:text-accent transition-colors">
                    {t("index.footer.lenders")}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t("index.footer.resources")}</h3>
              <ul className="space-y-2 text-sm text-primary-foreground/80">
                <li>
                  <Link to="/listings" className="hover:text-accent transition-colors">
                    {t("index.footer.deal_monitor")}
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-accent transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/resources/e-learning" className="hover:text-accent transition-colors">
                    E-Learning
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t("index.footer.contact")}</h3>
              <p className="text-sm text-primary-foreground/80 mb-4">
                {t("index.footer.contact_desc")}
              </p>
              <Link to="/contact">
                <Button size="sm" variant="accent">
                  {t("index.footer.contact_team")}
                </Button>
              </Link>
            </div>
          </div>
          <div className="border-t border-primary-foreground/20 mt-12 pt-8">
            <div className="flex flex-wrap justify-center gap-4 text-sm text-primary-foreground/60 mb-4">
              <Link to="/terms" className="hover:text-accent transition-colors">
                {t("index.footer.terms")}
              </Link>
              <span className="text-primary-foreground/40">|</span>
              <Link to="/privacy" className="hover:text-accent transition-colors">
                {t("index.footer.privacy")}
              </Link>
              <span className="text-primary-foreground/40">|</span>
              <Link to="/cookies" className="hover:text-accent transition-colors">
                {t("index.footer.cookies")}
              </Link>
              <span className="text-primary-foreground/40">|</span>
              <ManageCookiesButton />
            </div>
            <p className="text-center text-sm text-primary-foreground/60">{t("index.footer.copyright")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
